import type { Room } from 'livekit-client'
import { mintFeedToken } from './api'
import { explain } from '@/shared/api/problem'
import type { LocationOutcome } from '@/shared/geo/geolocation'

/**
 * The broadcast, living OUTSIDE React.
 *
 * ## Why this is a module singleton and not a hook's state
 *
 * It used to be component state, and the cleanup disconnected the room on
 * unmount. Navigating from Go Live to the diary therefore took the camera off
 * air — while the panel said *"you do not have to stay on this page"*, which was
 * false. **This tab is the camera.** The recording is server-side and does not
 * need a viewer, but it absolutely needs the publisher connected, and a producer
 * who wants to glance at the diary mid-rally should not have to choose.
 *
 * So the session lives here, keyed to nothing but itself, and React subscribes.
 * Unmounting a panel detaches a `<video>` element; it does not end a broadcast.
 * Exactly three things do: pressing Stop, closing the tab, and the media server
 * dropping us.
 *
 * ## One at a time, deliberately
 *
 * A device has one camera and a person has one attention. Two concurrent
 * broadcasts from one tab would be two `getUserMedia` calls fighting over the
 * same hardware, and the second would fail with `NotReadableError` blaming an
 * app the user has already closed. Starting a second session stops the first,
 * and the UI says so before it happens.
 */

export type BroadcastPhase =
  | 'idle' | 'preview' | 'authorising' | 'connecting' | 'live' | 'stopping' | 'error'

export interface BroadcastSnapshot {
  phase: BroadcastPhase
  /** Null unless a camera is open or on air. */
  feedId: string | null
  feedTitle: string | null
  eventTitle: string | null
  error: string | null
  viewers: number
  /** Epoch ms the broadcast started; null when not live. Elapsed is derived. */
  startedAt: number | null
  deviceLabel: string | null
  location: LocationOutcome | null
  /** True after `stop()` ran to completion — never after a connection was cut. */
  stoppedCleanly: boolean
}

const IDLE: BroadcastSnapshot = {
  phase: 'idle', feedId: null, feedTitle: null, eventTitle: null, error: null,
  viewers: 0, startedAt: null, deviceLabel: null, location: null, stoppedCleanly: false,
}

let snapshot: BroadcastSnapshot = IDLE
const listeners = new Set<() => void>()

let room: Room | null = null
let media: MediaStream | null = null
/** Bumped by stop() and by starting again — invalidates an in-flight goLive. */
let run = 0

function set(patch: Partial<BroadcastSnapshot>) {
  // A NEW object every time: `useSyncExternalStore` compares by identity and
  // mutating in place would render nothing.
  snapshot = { ...snapshot, ...patch }
  listeners.forEach((l) => l())
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function getSnapshot(): BroadcastSnapshot {
  return snapshot
}

/** The live `MediaStream`, for a panel to attach to its `<video>`. */
export function currentMedia(): MediaStream | null {
  return media
}

export function isBroadcasting(): boolean {
  return snapshot.phase === 'live' || snapshot.phase === 'connecting'
    || snapshot.phase === 'authorising'
}

function releaseCamera() {
  media?.getTracks().forEach((t) => t.stop())
  media = null
}

export function preflight(): { ok: boolean; reason: string | null; fix: string | null } {
  if (typeof window === 'undefined') return { ok: false, reason: null, fix: null }
  const secure = window.isSecureContext
  const devices = typeof navigator !== 'undefined' && !!navigator.mediaDevices
  if (!secure || !devices) {
    return {
      ok: false,
      reason: 'This browser will not hand over a camera on a plain http:// address that is not localhost.',
      fix: 'Open this page over https, or on the machine itself at localhost. On a phone, ask '
         + 'whoever set up the console for the https address — the certificate warning is '
         + 'expected and the connection is still encrypted.',
    }
  }
  if (!('RTCPeerConnection' in window)) {
    return {
      ok: false,
      reason: 'This browser has no WebRTC support.',
      fix: 'Use Chrome, Edge, Firefox or Safari 12+.',
    }
  }
  return { ok: true, reason: null, fix: null }
}

export async function openCamera(
  target: { feedId: string; feedTitle: string; eventTitle?: string | null },
  opts: { audioOnly?: boolean; facingMode?: string } = {},
): Promise<void> {
  const check = preflight()
  if (!check.ok) { set({ phase: 'error', error: check.reason }); return }

  // Starting a second session ends the first. See the header.
  if (isBroadcasting() && snapshot.feedId !== target.feedId) await stop()

  // Always release the previous capture first. On most phones a second
  // getUserMedia while the first is held fails with NotReadableError, which
  // surfaces as "the camera is in use by another app" and blames the wrong thing.
  releaseCamera()
  set({
    error: null,
    stoppedCleanly: false,
    feedId: target.feedId,
    feedTitle: target.feedTitle,
    eventTitle: target.eventTitle ?? null,
    location: null,
  })

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: opts.audioOnly ? false : { facingMode: opts.facingMode ?? 'environment', width: { ideal: 1280 } },
      audio: true,
    })
    media = stream
    const track = stream.getVideoTracks()[0] ?? stream.getAudioTracks()[0]
    set({ phase: 'preview', deviceLabel: track?.label ?? null })
  } catch (e) {
    set({ phase: 'error', error: cameraProblem(e) })
  }
}

export async function goLive(place: LocationOutcome): Promise<void> {
  const feedId = snapshot.feedId
  if (!feedId || !media) return
  const mine = ++run
  const cancelled = () => run !== mine

  set({ phase: 'authorising', error: null, location: place })
  let opened: Room | null = null
  try {
    const grant = await mintFeedToken(feedId, 'PUBLISHER', {
      deviceLabel: snapshot.deviceLabel ?? undefined,
      location: place.ok ? place.location : undefined,
      locationUnavailableReason: place.ok ? undefined : place.reason,
    })
    if (cancelled()) return
    if (!grant.wsUrl) { set({ phase: 'error', error: NO_MEDIA_SERVER }); return }

    set({ phase: 'connecting' })
    const { Room: LiveKitRoom, RoomEvent, LocalVideoTrack, LocalAudioTrack, Track } =
      await import('livekit-client')
    if (cancelled()) return

    /**
     * `dynacast` IS OFF, and that is a recording decision.
     *
     * Dynacast stops sending simulcast layers nobody is subscribed to. On a
     * publisher that is being recorded it produced a track whose upper layers
     * were declared but had no SSRC — `[('q',320,ssrc), ('h',640,none),
     * ('f',1280,none)]` — so egress subscribed to a quality that was not flowing,
     * never received a frame it could start on, and left the pipeline PAUSED for
     * the whole broadcast. The failure is intermittent by nature: it depends on
     * which layer is live at the instant egress subscribes, so it recorded fine
     * one attempt in three and looked like a backend fault.
     *
     * `adaptiveStream` stays on. It is a SUBSCRIBER-side optimisation (pause
     * tracks whose video element is not visible) and has no bearing on what this
     * participant sends.
     */
    opened = new LiveKitRoom({ adaptiveStream: true, dynacast: false })
    await opened.connect(grant.wsUrl, grant.token)
    // Checked AFTER connect and BEFORE the module ref is stored: if we lost the
    // race, this local is the only reference to a live connection and dropping it
    // silently would leave a camera on air with nothing able to stop it.
    if (cancelled()) { await opened.disconnect(); return }
    room = opened

    const live = opened
    const recount = () => set({ viewers: live.remoteParticipants.size })
    live.on(RoomEvent.ParticipantConnected, recount)
      .on(RoomEvent.ParticipantDisconnected, recount)
      .on(RoomEvent.Disconnected, () => {
        // Force-ended, token expired, transport lost. Release the camera —
        // leaving it lit under a panel reading "Camera is off" was a bug.
        room = null
        releaseCamera()
        set({ phase: 'idle', startedAt: null, viewers: 0 })
      })
    recount()

    /**
     * `source` IS NOT OPTIONAL. A `LocalVideoTrack` wrapped around a raw
     * `MediaStreamTrack` is `Track.Source.Unknown`, the publisher token grants
     * `canPublishSources: ["camera","microphone"]`, and LiveKit refuses every
     * track with "insufficient permissions" — which reads like a backend
     * authorisation problem and is not one.
     *
     * `videoCodec` IS NOT OPTIONAL EITHER, and it is the more expensive one to
     * get wrong because nothing complains. livekit-client defaults to **VP8**;
     * the recording is an **MP4**, and MP4 cannot carry VP8 (LiveKit's file
     * output offers MP4 and OGG, so there is no other container to move to).
     * The broadcast looks perfect — it connects, viewers watch it, the tile is
     * live — and the egress pipeline sits PAUSED for its entire length. The only
     * trace is a FAILED recording afterwards saying "Stop called before pipeline
     * could start", which describes the stop and not the cause. One real
     * broadcast was lost exactly this way.
     *
     * H.264 is also what Safari and iOS want, so this costs nothing elsewhere.
     */
    for (const t of media.getVideoTracks()) {
      await live.localParticipant.publishTrack(new LocalVideoTrack(t), {
        source: Track.Source.Camera,
        videoCodec: 'h264',
        /**
         * ONE LAYER. Simulcast exists so a viewer on a poor connection can take a
         * smaller stream; the cost here is that egress has to pick a layer, and
         * the layer it picks may not be the one being sent. A single encoding
         * removes the choice, and with it the class of failure above.
         *
         * The trade is real and it is the right way round: every viewer of this
         * feature is an operator at a desk, and the recording is the artefact the
         * whole feature exists to produce. A slightly heavier stream for a viewer
         * is worth a broadcast that is always recorded.
         */
        simulcast: false,
        /**
         * No backup codec. The browser was offering `video/vp8` alongside H.264
         * as a fallback, and VP8 is the codec MP4 cannot carry — the bug that lost
         * the first broadcast. An automatic fallback into it would lose another
         * one just as silently.
         */
        backupCodec: false,
      })
    }
    for (const t of media.getAudioTracks()) {
      await live.localParticipant.publishTrack(new LocalAudioTrack(t), {
        source: Track.Source.Microphone,
      })
    }
    if (cancelled()) { await live.disconnect(); return }
    set({ phase: 'live', startedAt: Date.now() })
  } catch (e) {
    if (cancelled()) return
    await opened?.disconnect().catch(() => {})
    room = null
    set({ phase: 'error', error: explain(e) })
  }
}

export async function stop(): Promise<void> {
  run += 1
  set({ phase: 'stopping' })
  try { await room?.disconnect() } finally {
    room = null
    releaseCamera()
    set({ phase: 'idle', startedAt: null, viewers: 0, stoppedCleanly: true })
  }
}

/** Clears a finished session's banner without touching a live one. */
export function dismiss(): void {
  if (snapshot.phase === 'idle') set({ ...IDLE })
}

export const NO_MEDIA_SERVER =
  'This build has no media server. Everything else on this screen is live; the picture is not.'

function cameraProblem(e: unknown): string {
  const name = (e as { name?: string } | null)?.name
  switch (name) {
    case 'NotAllowedError':
      return 'Permission was refused. Allow camera and microphone for this site, then try again.'
    case 'NotFoundError':
      return 'No camera or microphone was found on this device.'
    case 'NotReadableError':
      return 'The camera is already in use by another app. Close it and try again.'
    default:
      return `Could not open the camera: ${explain(e)}`
  }
}

/**
 * Closing the tab IS the end of the broadcast, and it should be a clean one.
 *
 * `pagehide` rather than `beforeunload`: the latter does not fire reliably on
 * mobile Safari, which is the platform most likely to be holding the camera.
 * A clean disconnect makes the server's `participant_left` webhook fire now
 * rather than after reconciliation's grace window, so the feed ends and the
 * recording is finalised in seconds instead of minutes.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (room) void room.disconnect()
    media?.getTracks().forEach((t) => t.stop())
  })
}
